import { sum } from 'lodash';
import { useMemo } from 'react';

import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { useBalanceSheetSuggestionsBannerDismissed } from '@/modules/estate/estateAIOnboarding/hooks/useBalanceSheetSuggestionsBannerDismissed';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { useTrowser } from '@/modules/navigation/trowsers/trowser.context';
import { TrowserKey } from '@/modules/navigation/trowsers/trowser.types';
import { getNodes } from '@/utils/graphqlUtils';

import { useReviewSuggestionCountsQuery } from './ReviewSuggestions.generated';
import { getReviewSuggestionCounts } from './ReviewSuggestions.utils';
import {
  ReviewSuggestionsBanner,
  ReviewSuggestionsBannerVariant,
} from './ReviewSuggestionsBanner';
import { ReviewSuggestionsTrowserMount } from './ReviewSuggestionsTrowserMount';

interface ReviewSuggestionsBannerEntryPointProps {
  mountTrowser?: boolean;
  showBanner?: boolean;
}

export function ReviewSuggestionsBannerEntryPoint({
  mountTrowser = true,
  showBanner = true,
}: ReviewSuggestionsBannerEntryPointProps = {}) {
  const canEdit = useCanEditHouseholdData();
  const { defaultFirstToDieClientId, householdId } =
    useHouseholdDetailsContext();
  const reviewSuggestionsTrowser = useTrowser(TrowserKey.ReviewSuggestions);
  const { data } = useReviewSuggestionCountsQuery({
    variables: { householdId: householdId ?? '' },
    skip: !canEdit || !householdId,
    fetchPolicy: 'cache-and-network',
  });
  const suggestions = useMemo(
    () => getNodes(data?.aiSuggestions),
    [data?.aiSuggestions]
  );
  const counts = getReviewSuggestionCounts(
    suggestions,
    defaultFirstToDieClientId ?? undefined
  );
  const currentSuggestionIds = useMemo(
    () => suggestions.map(({ id }) => id),
    [suggestions]
  );
  const { isDismissed, dismiss } = useBalanceSheetSuggestionsBannerDismissed({
    householdId: householdId ?? '',
    currentSuggestionIds,
  });
  const suggestionCount = sum(Object.values(counts));
  const hasReviewableSuggestions =
    canEdit && suggestionCount > 0 && !isDismissed;

  return (
    <>
      {showBanner && hasReviewableSuggestions && (
        <ReviewSuggestionsBanner
          onDismiss={dismiss}
          onReview={reviewSuggestionsTrowser.open}
          suggestionCount={suggestionCount}
          variant={ReviewSuggestionsBannerVariant.Overview}
        />
      )}
      {mountTrowser && canEdit && <ReviewSuggestionsTrowserMount />}
    </>
  );
}
